package org.example.project.ui.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.example.project.client.api.ActivityApiClient
import org.example.project.client.api.createDraftWithSession
import org.example.project.client.api.updateActivityWithSession
import org.example.project.client.error.ApiClientException
import org.example.project.client.error.MissingCsrfTokenException
import org.example.project.client.model.ActivityFormValidationResult
import org.example.project.client.model.ActivityFormValidator
import org.example.project.client.model.ActivityResponse
import org.example.project.client.model.CreateActivityRequest
import org.example.project.client.model.UpdateActivityRequest
import org.example.project.client.session.SessionRepository

data class ActivityFormUiState(
    val activityToEdit: ActivityResponse? = null,
    val name: String = "",
    val description: String = "",
    val category: String = "",
    val minAgeText: String = "",
    val maxAgeText: String = "",
    val durationText: String = "",
    val videoUrlText: String = "",
    val validationErrors: ActivityFormValidationResult = ActivityFormValidator.validate("", "", "", "", "", "", ""),
    val hasSubmittedOnce: Boolean = false,
    val isSaving: Boolean = false
) {
    val isEdit: Boolean
        get() = activityToEdit != null
}

class ActivityFormViewModel(
    activityToEdit: ActivityResponse?,
    private val sessionRepository: SessionRepository,
    private val activityApiClient: ActivityApiClient
) : ViewModel() {

    private val _uiState = MutableStateFlow(
        ActivityFormUiState(
            activityToEdit = activityToEdit,
            name = activityToEdit?.name ?: "",
            description = activityToEdit?.description ?: "",
            category = activityToEdit?.category ?: "",
            minAgeText = activityToEdit?.minAge?.toString() ?: "",
            maxAgeText = activityToEdit?.maxAge?.toString() ?: "",
            durationText = activityToEdit?.durationMinutes?.toString() ?: "",
            videoUrlText = activityToEdit?.videoUrl ?: ""
        )
    )
    val uiState: StateFlow<ActivityFormUiState> = _uiState.asStateFlow()

    fun onNameChange(value: String) {
        _uiState.update { current ->
            val updated = current.copy(name = value)
            if (updated.hasSubmittedOnce) runValidation(updated) else updated
        }
    }

    fun onDescriptionChange(value: String) {
        _uiState.update { current ->
            val updated = current.copy(description = value)
            if (updated.hasSubmittedOnce) runValidation(updated) else updated
        }
    }

    fun onCategoryChange(value: String) {
        _uiState.update { current ->
            val updated = current.copy(category = value)
            if (updated.hasSubmittedOnce) runValidation(updated) else updated
        }
    }

    fun onMinAgeChange(value: String) {
        _uiState.update { current ->
            val updated = current.copy(minAgeText = value)
            if (updated.hasSubmittedOnce) runValidation(updated) else updated
        }
    }

    fun onMaxAgeChange(value: String) {
        _uiState.update { current ->
            val updated = current.copy(maxAgeText = value)
            if (updated.hasSubmittedOnce) runValidation(updated) else updated
        }
    }

    fun onDurationChange(value: String) {
        _uiState.update { current ->
            val updated = current.copy(durationText = value)
            if (updated.hasSubmittedOnce) runValidation(updated) else updated
        }
    }

    fun onVideoUrlChange(value: String) {
        _uiState.update { current ->
            val updated = current.copy(videoUrlText = value)
            if (updated.hasSubmittedOnce) runValidation(updated) else updated
        }
    }

    private fun runValidation(state: ActivityFormUiState): ActivityFormUiState {
        val result = ActivityFormValidator.validate(
            name = state.name,
            description = state.description,
            category = state.category,
            minAgeText = state.minAgeText,
            maxAgeText = state.maxAgeText,
            durationMinutesText = state.durationText,
            videoUrlText = state.videoUrlText
        )
        return state.copy(validationErrors = result)
    }

    fun save(
        onSuccess: (ActivityResponse) -> Unit,
        onError: (String) -> Unit,
        onReauthenticate: () -> Unit
    ) {
        val validatedState = runValidation(_uiState.value.copy(hasSubmittedOnce = true))
        _uiState.value = validatedState

        if (!validatedState.validationErrors.isValid) {
            return
        }

        val minAge = validatedState.minAgeText.trim().toInt()
        val maxAge = validatedState.maxAgeText.trim().toInt()
        val duration = validatedState.durationText.trim().toInt()
        val currentEdit = validatedState.activityToEdit
        val sanitizedVideoUrl = validatedState.videoUrlText.trim().takeIf { it.isNotEmpty() }

        _uiState.update { it.copy(isSaving = true) }

        viewModelScope.launch {
            try {
                val saved = if (currentEdit != null) {
                    activityApiClient.updateActivityWithSession(
                        id = currentEdit.id,
                        request = UpdateActivityRequest(
                            name = validatedState.name.trim(),
                            description = validatedState.description.trim(),
                            minAge = minAge,
                            maxAge = maxAge,
                            category = validatedState.category.trim(),
                            durationMinutes = duration,
                            videoUrl = sanitizedVideoUrl
                        ),
                        sessionRepository = sessionRepository
                    )
                } else {
                    activityApiClient.createDraftWithSession(
                        request = CreateActivityRequest(
                            name = validatedState.name.trim(),
                            description = validatedState.description.trim(),
                            minAge = minAge,
                            maxAge = maxAge,
                            category = validatedState.category.trim(),
                            durationMinutes = duration,
                            videoUrl = sanitizedVideoUrl
                        ),
                        sessionRepository = sessionRepository
                    )
                }
                onSuccess(saved)
            } catch (e: MissingCsrfTokenException) {
                onError("Wymagana ponowna autoryzacja sesji (CSRF). Zaloguj się ponownie.")
                onReauthenticate()
            } catch (e: ApiClientException) {
                onError("Błąd zapisu: ${e.message}")
            } catch (e: Throwable) {
                onError("Błąd połączenia: ${e.message}")
            } finally {
                _uiState.update { it.copy(isSaving = false) }
            }
        }
    }
}
